import type { AccountRole } from '../api/types';

/**
 * Mirrors common-microservice/app/models/roles.rb.
 *
 * ADMIN administers the shop. STAFF is every non-buyer role — ADMIN plus the
 * feature-scoped roles — and answers only "may this person enter /admin at
 * all". Each feature role has its own set (the role plus the admins):
 * SALES_STAFF for Relations (docs/superpowers/specs/2026-09-06-relations-design.md),
 * RETOUCH_STAFF for the retouch and Variants work surfaces
 * (docs/superpowers/specs/2026-09-14-retoucher-role-and-variants-queue-design.md).
 * Gate a feature on its own set, never on isStaff. Never widen ADMIN_ROLES.
 */
export const ADMIN_ROLES: readonly AccountRole[] = ['account_admin', 'global_admin'];
export const SALES_STAFF_ROLES: readonly AccountRole[] = ['account_sales', ...ADMIN_ROLES];
export const RETOUCH_STAFF_ROLES: readonly AccountRole[] = ['account_retoucher', ...ADMIN_ROLES];
export const STAFF_ROLES: readonly AccountRole[] = ['account_sales', 'account_retoucher', ...ADMIN_ROLES];

// Plain strings, not AccountRole: user rows from the API type `roles` as
// string[] (the backend is the authority on the vocabulary), so callers pass
// them through without a cast.
type Roles = readonly string[] | undefined | null;

const hasAny = (roles: Roles, set: readonly AccountRole[]): boolean =>
  (roles ?? []).some((r) => (set as readonly string[]).includes(r));

export const isAdmin = (roles: Roles): boolean => hasAny(roles, ADMIN_ROLES);
export const isStaff = (roles: Roles): boolean => hasAny(roles, STAFF_ROLES);
export const isSalesStaff = (roles: Roles): boolean => hasAny(roles, SALES_STAFF_ROLES);
export const isRetouchStaff = (roles: Roles): boolean => hasAny(roles, RETOUCH_STAFF_ROLES);
